import FullPageLoader from '@components/core/full-page-loader/full-page-loader';
import { PageLoadError } from '@components/page-load-error';
import {
  getAccountsQueryOptions,
  getProductsQueryOptions,
} from '@features/accounts/api';
import { getCobrandQueryOptions } from '@features/navigation/api/get-cobrand';
import { getProfileQueryOptions } from '@features/user/api/get-profile';
import { getBetaFeaturesQueryOptions } from '@src/api/beta-features/get-beta-features';
import {
  APP_ACTIVE_KEY,
  ONBOARDING_ENTRY_KEY,
} from '@src/features/common-application/constants';
import { getCommonApplicationQueryOptions } from '@src/features/common-application/api/get-common-application';
import { endTrackedEntry } from '@src/features/common-application/hooks/use-track-application-entry';
import { useThemeStore } from '@src/stores/theme-store';
import { GetCobrandResponse } from '@src/types/get-cobrand-response';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { hasCommonApplicationV2Enabled } from '@utils';
import { z } from 'zod';
import CommonApplicationLayout from './-layouts/common-application-layout';

export const Route = createFileRoute(
  '/_authenticated-chromeless/common-application',
)({
  component: CommonApplicationLayout,
  errorComponent: ({ error }) => (
    <PageLoadError pageName='Common Application' error={error} />
  ),
  validateSearch: z.object({
    entry: z.literal('onboarding').optional(),
  }),
  beforeLoad: ({ context, search }) => {
    const data = context.queryClient.getQueryData(
      getBetaFeaturesQueryOptions.queryKey,
    );
    if (!data || !hasCommonApplicationV2Enabled(data)) {
      redirect({
        href: `${import.meta.env.VITE_BASE_URL}/common_application/choose`,
        throw: true,
      });
    }
    // Rails hands off to the wizard with ?entry=onboarding after the user picks
    // a checking option during registration. Treat it as deliberate entry so the
    // guard below lets it through; the index route picks the step to resume at.
    if (search.entry === 'onboarding') {
      try {
        sessionStorage.setItem(APP_ACTIVE_KEY, 'true');
        // The redirect below drops ?entry, so keep it for the layout's
        // analytics: a new draft entered this way is a registration start.
        sessionStorage.setItem(ONBOARDING_ENTRY_KEY, 'true');
        // A handoff starts a new visit, even if the last one ended with a
        // full page load to Rails, which never runs onLeave.
        endTrackedEntry();
      } catch {
        // Storage blocked (private browsing). The guard below will send the
        // user to the dashboard, where the resume banner still works.
      }
    }
    // Redirect to dashboard on post-login entry (new tab or after session expiry).
    if (!sessionStorage.getItem(APP_ACTIVE_KEY)) {
      redirect({ to: '/dashboard', throw: true });
    }
  },
  loader: async ({ context }) => {
    const [profile] = await Promise.all([
      context.queryClient.ensureQueryData(getProfileQueryOptions),
      context.queryClient.ensureQueryData(getCommonApplicationQueryOptions),
      context.queryClient.ensureQueryData(getAccountsQueryOptions),
      context.queryClient.ensureQueryData(getProductsQueryOptions),
    ]);
    // The server decides whether a common application can be opened; the
    // HYS tab explains why not and links to the Rails checking page.
    if (!profile.commonApplicationEligibility.canApply) {
      // The wizard won't open, so a later entry in this tab mustn't be
      // counted as this handoff.
      sessionStorage.removeItem(ONBOARDING_ENTRY_KEY);
      redirect({ to: '/dashboard/high-yield-savings', throw: true });
    }
  },
  // Leaving the wizard by any in-app navigation ends the visit for analytics.
  // Moving between steps, a step error, and a reload don't run this. It also
  // drops a handoff mark the layout never read, say after a loader error.
  onLeave: () => {
    endTrackedEntry();
    try {
      sessionStorage.removeItem(ONBOARDING_ENTRY_KEY);
    } catch {
      // Storage blocked: nothing was stored.
    }
  },
  wrapInSuspense: true,
  pendingComponent: () => {
    const partner = useThemeStore.use.partner();
    const data: GetCobrandResponse | undefined =
      Route.useRouteContext().queryClient.getQueryData(
        getCobrandQueryOptions({ partner }).queryKey,
      );
    return <FullPageLoader cobrandImage={data?.image} />;
  },
});
